import React, { useEffect, useState } from "react";
import { useRemoteRenderContext } from "../../../../classes/contexts/RemoteRenderContext";
import { PLANNING_STATES } from "../../../../classes/enums/PlanningStateEnum";
import { vertebraUtils } from "../../../../classes/utils/AnnotationsUtils";
import Button from "../../../../components/button/Button";
import LabeledSelect from "../../../../components/select/LabeledSelect";
import "../VertebraManagement.css";

const RemoveVertebra = ({ onClose }) => {
  const { client: renderClient } = useRemoteRenderContext();
  const [existingVertebrae, setExistingVertebrae] = useState({
    frontal: [],
    lateral: [],
  });
  const [selectedVertebra, setSelectedVertebra] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchExistingVertebrae = async () => {
      try {
        setIsLoading(true);
        const data = await renderClient.getDeletableVertebrae(
          PLANNING_STATES.VALIDATE
        );
        setExistingVertebrae(data);

        // Get vertebrae from whichever view(s) have data
        let vertebraNames = [];

        if (data.frontal.length > 0 && data.lateral.length > 0) {
          // Both views have data - use common vertebrae
          vertebraNames = data.frontal
            .filter(([name]) =>
              data.lateral.some(([lateralName]) => lateralName === name)
            )
            .map(([name]) => name);
        } else if (data.frontal.length > 0) {
          // Only frontal has data
          vertebraNames = data.frontal.map(([name]) => name);
        } else if (data.lateral.length > 0) {
          // Only lateral has data
          vertebraNames = data.lateral.map(([name]) => name);
        }

        if (vertebraNames.length > 0) {
          setSelectedVertebra(vertebraNames[0]);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchExistingVertebrae();
  }, [renderClient]);

  // Generate vertebra items from whichever view(s) have data
  // Backend provides Sacrum -> L5 -> ... -> C1, we need to reverse to C1 -> ... -> L5 -> Sacrum
  const vertebraItems = (() => {
    if (
      existingVertebrae.frontal.length > 0 &&
      existingVertebrae.lateral.length > 0
    ) {
      // Both views have data - use common vertebrae, reverse the order
      const commonVertebrae = existingVertebrae.frontal.filter(([name]) =>
        existingVertebrae.lateral.some(([lateralName]) => lateralName === name)
      );
      return commonVertebrae.reverse().map(([vertebra]) => ({
        value: vertebra,
        label: vertebraUtils.getVertebraLabel(vertebra),
      }));
    } else if (existingVertebrae.frontal.length > 0) {
      // Only frontal has data
      return existingVertebrae.frontal
        .slice()
        .reverse()
        .map(([vertebra]) => ({
          value: vertebra,
          label: vertebraUtils.getVertebraLabel(vertebra),
        }));
    } else if (existingVertebrae.lateral.length > 0) {
      // Only lateral has data
      return existingVertebrae.lateral
        .slice()
        .reverse()
        .map(([vertebra]) => ({
          value: vertebra,
          label: vertebraUtils.getVertebraLabel(vertebra),
        }));
    }
    return [];
  })();

  const handleVertebraChange = event => {
    const value = event.target.value;
    setSelectedVertebra(value);
  };

  const handleRemoveVertebra = React.useCallback(async () => {
    if (!selectedVertebra) {
      return;
    }

    try {
      const frontalData = existingVertebrae.frontal.find(
        ([name]) => name === selectedVertebra
      )?.[1];
      const lateralData = existingVertebrae.lateral.find(
        ([name]) => name === selectedVertebra
      )?.[1];

      // Build vertebra data with only the views that have data
      const vertebraData = {
        vertebraName: selectedVertebra,
      };

      if (frontalData) {
        vertebraData.frontal = frontalData;
      }
      if (lateralData) {
        vertebraData.lateral = lateralData;
      }

      // Make sure we have at least one view's data
      if (!frontalData && !lateralData) {
        return;
      }

      await renderClient.removeVertebra(PLANNING_STATES.VALIDATE, vertebraData);
      onClose?.();
    } catch (error) {}
  }, [selectedVertebra, existingVertebrae, renderClient, onClose]);

  if (isLoading) {
    return (
      <div className="vertebra-management-container">
        <div>Loading existing vertebrae...</div>
      </div>
    );
  }

  if (!vertebraItems.length) {
    return (
      <div className="vertebra-management-container">
        <div>No vertebrae to remove</div>
      </div>
    );
  }

  const isSingleVertebra = vertebraItems.length === 1;

  return (
    <div className="vertebra-management-container">
      <LabeledSelect
        label="Select Vertebra"
        items={vertebraItems}
        selectedItem={selectedVertebra}
        onChange={handleVertebraChange}
        disable={isLoading || isSingleVertebra}
      />
      <div className="vertebra-management-actions">
        <Button
          className="App-button"
          onClick={handleRemoveVertebra}
          disabled={!selectedVertebra}
          value="Remove Vertebra"
        />
      </div>
    </div>
  );
};

export default RemoveVertebra;
